import { NotificationList } from 'components/common/NotificationList'
import { put, takeLatest, call } from 'redux-saga/effects'
import {
  makeGetRequest,
  getRequest,
  makePutRequest,
  makePostRequest,
  makeDeleteRequest
} from 'utils/api'
import { formatResponse, formatErrorMessage } from 'utils/formatting'

import { toCompanyFormData } from 'components/Companies/serializers'
import { toTask } from 'utils/serializers'

import * as ACT from './actions'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { refreshNotifications } from 'utils/helpers'

function* fetchCompanyContacts(action) {
  try {
    const { id } = action.payload
    const url = `/v1/contacts/company/${id}`
    const contacts = yield makeGetRequest(url)
    yield put({
      type: ACT.COMPANY_CONTACTS_FETCH_SUCCESS,
      payload: {
        contacts
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Company Contacts',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchCompany(action) {
  try {
    const { id } = action.payload
    const url = `/v1/companies/${id}/details`
    const company = yield call(getRequest, url)
    yield put({
      type: ACT.COMPANY_FETCH_SUCCESS,
      payload: {
        company
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Company',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateCompany(action) {
  const { id, company, file } = action.payload
  try {
    const url = `/v1/companies/${id}/form`
    const response = yield makePutRequest(url, toCompanyFormData(company, file))
    yield put({
      type: ACT.COMPANY_UPDATE_SUCCESS,
      payload: {
        company: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Company updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Company',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchCompanyNotes(action) {
  try {
    const { id } = action.payload
    const url = `/v1/notes/company/${id}`
    const notes = yield makeGetRequest(url)

    yield put({
      type: ACT.COMPANY_NOTES_FETCH_SUCCESS,
      payload: {
        notes: notes
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Company Notes',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* addCompanyNote(action) {
  const { data } = action.payload
  try {
    const url = '/v1/notes'
    const note = yield makePostRequest(url, data)

    yield put({
      type: ACT.COMPANY_ADD_NOTE_SUCCESS,
      payload: {
        note
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note added successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue adding Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateCompanyNote(action) {
  const { data, noteId } = action.payload
  try {
    const url = `/v1/notes/${noteId}`
    const note = yield makePutRequest(url, data)

    yield put({
      type: ACT.COMPANY_UPDATE_NOTE_SUCCESS,
      payload: {
        note,
        noteId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteCompanyNote(action) {
  const { noteId } = action.payload
  try {
    const url = `/v1/notes/${noteId}`
    yield makeDeleteRequest(url)

    yield put({
      type: ACT.COMPANY_DELETE_NOTE_SUCCESS,
      payload: {
        noteId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note deleted successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* createTask(action) {
  const { companyId, task } = action.payload
  try {
    const url = '/v1/tasks'
    const taskObj = toTask(task)
    taskObj.companyId = companyId
    const response = yield makePostRequest(url, taskObj)

    yield put({
      type: ACT.CREATE_COMPANY_TASK_SUCCESS,
      payload: {
        tasks: response
      }
    })
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        level: 'success',
        title: 'Task created successfully'
      }
    })
    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating Task for Company',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchCompanyTasks(action) {
  try {
    const { id, name } = action.payload
    const url = `/v1/tasks/company/${id}`
    const response = yield makeGetRequest(url)

    yield put({
      type: ACT.COMPANY_TASKS_FETCH_SUCCESS,
      payload: {
        tasks: response,
        companyName: name
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Company tasks',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* markTaskCompleted(action) {
  try {
    const { id } = action.payload
    const url = `/v1/tasks/${id}/markcompleted`
    const task = yield makePutRequest(url)

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task was marked as completed!',
        level: 'success'
      }
    })
    yield put({
      type: ACT.COMPANY_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        task
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue marking the Task as completed',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* markTaskNotCompleted(action) {
  try {
    const { id } = action.payload
    const url = `/v1/tasks/${id}/marknotcompleted`
    const task = yield makePutRequest(url)

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task was marked as not completed!',
        level: 'success'
      }
    })
    yield put({
      type: ACT.COMPANY_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        task
      }
    })
    yield put({
      type: ACT.COMPANY_TASK_MARK_NOT_COMPLETED_SUCCESS
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue marking the Task as not completed',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateTask(action) {
  const { task } = action.payload
  try {
    const url = `/v1/tasks/${task.id}`
    const taskObj = toTask(task, {}, true)
    taskObj.dealId = task.dealId

    const response = yield makePutRequest(url, taskObj)

    yield put({
      type: ACT.UPDATE_COMPANY_TASK_SUCCESS,
      payload: {
        task: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task updated successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteTask(action) {
  const { task } = action.payload
  try {
    const url = `/v1/tasks/${task.id}`
    yield makeDeleteRequest(url)

    yield put({
      type: ACT.DELETE_COMPANY_TASK_SUCCESS,
      payload: {
        task
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task deleted successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

const companySagas = [
  takeLatest(ACT.COMPANY_CONTACTS_FETCH_REQUESTED, fetchCompanyContacts),
  takeLatest(ACT.COMPANY_FETCH_REQUESTED, fetchCompany),
  takeLatest(ACT.COMPANY_UPDATE_REQUESTED, updateCompany),
  takeLatest(ACT.COMPANY_NOTES_FETCH_REQUESTED, fetchCompanyNotes),
  takeLatest(ACT.COMPANY_ADD_NOTE_REQUESTED, addCompanyNote),
  takeLatest(ACT.COMPANY_DELETE_NOTE_REQUESTED, deleteCompanyNote),
  takeLatest(ACT.COMPANY_UPDATE_NOTE_REQUESTED, updateCompanyNote),
  takeLatest(ACT.CREATE_COMPANY_TASK_REQUESTED, createTask),
  takeLatest(ACT.COMPANY_TASKS_FETCH_REQUESTED, fetchCompanyTasks),
  takeLatest(ACT.COMPANY_TASK_MARK_COMPLETED_REQUESTED, markTaskCompleted),
  takeLatest(ACT.COMPANY_TASK_MARK_NOT_COMPLETED_REQUESTED, markTaskNotCompleted),
  takeLatest(ACT.UPDATE_COMPANY_TASK_REQUESTED, updateTask),
  takeLatest(ACT.DELETE_COMPANY_TASK_REQUESTED, deleteTask)
]

export default companySagas
